// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
//
// HOW LONG WAS IT ON THE SHELF — a distribution, and it has to be.
//
// The gap between "first went on sale" and "was recalled" is heavily skewed
// with a very long tail, so a mean would be dominated by a handful of products
// that sat on shelves for a decade and would describe none of them. The tail is
// the interesting part, which is exactly the argument for a distribution form
// over any single summary number.
//
// The recalls with no usable sale range are shown as their own labelled block
// rather than dropped. Dropping them would quietly shrink the denominator and
// make the distribution look more complete than the register is.

import { getBundle } from '../data';
import { duration, num } from '../format';
import { navigate } from '../router';
import { chartFrame, labelChart, linear, markTip, niceTicks, svgEl } from '../components/svg';
import { glossaryButton } from '../glossary';

export function renderShelf(host: HTMLElement): void {
  const { stats, hazards, hazardById, meta } = getBundle();
  const shelf = stats.shelf;
  const coverage = (100 * shelf.n) / (shelf.n + shelf.unknown);

  host.innerHTML = `
    <div class="view-head">
      <h2>How long a product is on sale before it is recalled</h2>
      <p>The register publishes the dates a product was available for sale. Subtract the start of that range
      from the recall date and you get something the ACCC never publishes: the
      ${glossaryButton('onSale', 'time on sale')} before the recall. The median is
      <strong>${duration(shelf.median)}</strong>, across ${num(shelf.n)} recalls where the range is usable —
      ${coverage.toFixed(0)}% of the register.</p>
    </div>
    <div id="hist"></div>
    <div class="grid-2" style="margin-top:var(--space-lg)">
      <div id="byhaz"></div>
      <div class="card">
        <div class="card-title">What this measures, and what it does not</div>
        <div class="card-sub">Worth reading before quoting a number from this chart.</div>
        <ul class="small" style="margin:var(--space-md) 0 0 1.1rem;display:grid;gap:.5rem">
          <li>It is the time the product was <strong>available to buy</strong>, not the time anyone knew it was
          dangerous. A supplier may have acted within days of learning of a fault on a product that had been
          selling for years.</li>
          <li>${num(shelf.unknown)} recalls (${(100 - coverage).toFixed(0)}%) have no usable sale range —
          it is absent, open-ended, or starts after the recall date. They are excluded from the distribution
          and counted here rather than quietly dropped.</li>
          <li>A long tail is expected and is not by itself a failure: durable goods legitimately sell for years
          before a rare fault emerges.</li>
        </ul>
      </div>
    </div>
  `;

  host.querySelector<HTMLElement>('#hist')!.append(histogram(shelf, hazardById));
  host.querySelector<HTMLElement>('#byhaz')!.append(byHazard(shelf, hazards));
  void meta;
}

const W = 900;
const H = 380;
const M = { top: 16, right: 20, bottom: 66, left: 56 };

function binLabel(from: number, to: number): string {
  if (to === -1) return `${duration(from)}+`;
  if (from === 0) return `under ${duration(to)}`;
  return `${duration(from)}–${duration(to)}`;
}

function histogram(
  shelf: ReturnType<typeof getBundle>['stats']['shelf'],
  hazardById: ReturnType<typeof getBundle>['hazardById'],
): HTMLElement {
  const { figure, mount } = chartFrame({
    title: 'Time on sale before the recall',
    subtitle: 'Bins are uneven on purpose — the data spans days to decades. Click a bar to search those recalls.',
    scroll: true,
    minWidth: 640,
  });

  const svg = svgEl('svg', { viewBox: `0 0 ${W} ${H}`, width: W, height: H, preserveAspectRatio: 'xMidYMid meet' });
  svg.style.minWidth = '640px';
  labelChart(svg, 'Distribution of time on sale before recall',
    'A histogram with uneven bins from under a month to ten years and over, with a long tail to the right.');

  const max = Math.max(...shelf.bins.map((b) => b.count));
  const y = linear([0, max], [H - M.bottom, M.top]);
  const bw = (W - M.left - M.right) / shelf.bins.length;

  for (const t of niceTicks(max, 5)) {
    svg.append(svgEl('line', { x1: M.left, x2: W - M.right, y1: y(t).toFixed(2), y2: y(t).toFixed(2), class: 'grid-line' }));
    const lab = svgEl('text', { x: M.left - 8, y: (y(t) + 4).toFixed(2), class: 'axis-text', 'text-anchor': 'end' });
    lab.textContent = num(t);
    svg.append(lab);
  }

  shelf.bins.forEach((b, i) => {
    const x = M.left + i * bw;
    const g = svgEl('g', { class: 'mark', role: 'button', tabindex: '0', style: 'cursor:pointer' });
    g.append(svgEl('rect', {
      x: (x + bw * 0.12).toFixed(2), y: y(b.count).toFixed(2),
      width: (bw * 0.76).toFixed(2), height: Math.max(0, H - M.bottom - y(b.count)).toFixed(2),
      fill: 'var(--accent-primary)', rx: 3,
    }));

    const top = Object.entries(b.byHazard)
      .map(([k, n]) => ({ hz: hazardById.get(Number(k)), n }))
      .filter((z) => z.hz)
      .sort((a, b2) => b2.n - a.n)
      .slice(0, 3)
      .map((z) => `${z.hz!.label}: ${z.n}`);

    markTip(g,
      `${binLabel(b.from, b.to)}\n${num(b.count)} recall${b.count === 1 ? '' : 's'} (${((100 * b.count) / shelf.n).toFixed(1)}%)\n${top.join('\n')}`,
      `${binLabel(b.from, b.to)}: ${num(b.count)} recalls`);

    const label = svgEl('text', {
      x: (x + bw / 2).toFixed(2), y: H - M.bottom + 16,
      class: 'axis-text', 'text-anchor': 'end',
      transform: `rotate(-34 ${(x + bw / 2).toFixed(2)} ${H - M.bottom + 16})`,
      'pointer-events': 'none',
    });
    label.textContent = binLabel(b.from, b.to);
    svg.append(g, label);

    const go = () => navigate('search', { from: b.from, to: b.to });
    g.addEventListener('click', go);
    g.addEventListener('keydown', (e) => {
      const ev = e as KeyboardEvent;
      if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); go(); }
    });
  });

  svg.append(svgEl('line', { x1: M.left, x2: W - M.right, y1: H - M.bottom, y2: H - M.bottom, class: 'axis-line' }));

  const axisTitle = svgEl('text', { x: W / 2, y: H - 6, class: 'axis-text', 'text-anchor': 'middle' });
  axisTitle.textContent = 'Time between going on sale and being recalled';
  svg.append(axisTitle);

  mount.append(svg);
  return figure;
}

function byHazard(
  shelf: ReturnType<typeof getBundle>['stats']['shelf'],
  hazards: ReturnType<typeof getBundle>['hazards'],
): HTMLElement {
  const rows = shelf.byHazardMedian
    .map((r) => ({ ...r, hz: hazards.find((h) => h.id === r.hazard)! }))
    .filter((r) => r.hz)
    .sort((a, b) => b.median - a.median);

  const { figure, mount } = chartFrame({
    title: 'Median time on sale, by hazard',
    subtitle: 'Only hazard classes with at least 20 measurable recalls are shown, so a median is not drawn from a handful of cases.',
  });

  const RW = 460;
  const RH = rows.length * 30 + 16;
  const svg = svgEl('svg', { viewBox: `0 0 ${RW} ${RH}`, width: RW, height: RH, preserveAspectRatio: 'xMinYMin meet' });
  labelChart(svg, 'Median time on sale before recall, by hazard class',
    'A horizontal bar chart ranking hazard classes by their median time on sale.');

  const max = Math.max(...rows.map((r) => r.median), 1);
  const LW = 168;
  const x = linear([0, max], [LW, RW - 62]);

  rows.forEach((r, i) => {
    const y = 8 + i * 30;
    const label = svgEl('text', { x: LW - 8, y: y + 18, class: 'axis-text', 'text-anchor': 'end' });
    label.textContent = r.hz.label.length > 24 ? `${r.hz.label.slice(0, 23)}…` : r.hz.label;
    svg.append(label);

    const bar = svgEl('rect', {
      x: LW, y: y + 5, width: Math.max(1, x(r.median) - LW).toFixed(2), height: 18, rx: 3,
      fill: r.hz.colour, class: 'mark',
    });
    markTip(bar, `${r.hz.label}\nMedian ${duration(r.median)} on sale\nfrom ${num(r.n)} measurable recalls`,
      `${r.hz.label}: median ${duration(r.median)} on sale, from ${num(r.n)} recalls`);
    svg.append(bar);

    const val = svgEl('text', { x: x(r.median) + 6, y: y + 18, class: 'axis-text' });
    val.textContent = duration(r.median);
    svg.append(val);
  });

  mount.append(svg);
  return figure;
}
